import { Feather } from "@expo/vector-icons";
import { useEffect, useMemo, useState } from "react";
import {
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import type { ProductPlacement } from "../../types";
import { useShop } from "./ShopProvider";

export default function ProductSpotlight({
  placements,
  currentTimeSeconds,
  compact = false,
}: {
  placements?: ProductPlacement[];
  currentTimeSeconds: number;
  compact?: boolean;
}) {
  const { addToCart, buyNow, cartCount, openCart, openProduct } = useShop();
  const [dismissedPlacementId, setDismissedPlacementId] = useState("");
  const active = useMemo(
    () =>
      (placements || []).find(
        (placement) =>
          placement.enabled !== false &&
          placement.stock > 0 &&
          currentTimeSeconds >= placement.startSeconds &&
          currentTimeSeconds < placement.endSeconds,
      ),
    [currentTimeSeconds, placements],
  );

  useEffect(() => {
    if (active?.placementId !== dismissedPlacementId)
      setDismissedPlacementId("");
  }, [active?.placementId]);

  if (!active || dismissedPlacementId === active.placementId) return null;

  return (
    <View
      pointerEvents="box-none"
      style={[styles.layer, compact && styles.compactLayer]}
    >
      <View style={[styles.card, compact && styles.compactCard]}>
        <Pressable
          style={styles.dismiss}
          onPress={() => setDismissedPlacementId(active.placementId)}
        >
          <Feather name="x" size={14} color="#c7d0cc" />
        </Pressable>
        <Pressable
          style={styles.productMain}
          onPress={() => openProduct(active)}
        >
          {active.imageUrl ? (
            <Image
              source={{ uri: active.imageUrl }}
              style={[styles.image, compact && styles.compactImage]}
            />
          ) : (
            <View
              style={[
                styles.image,
                styles.imageFallback,
                compact && styles.compactImage,
              ]}
            >
              <Feather
                name="shopping-bag"
                size={compact ? 18 : 23}
                color="#d7b65d"
              />
            </View>
          )}
          <View style={styles.copy}>
            <View style={styles.badgeRow}>
              <Text style={styles.timeBadge}>视频同款</Text>
              {active.badge ? (
                <Text style={styles.offerBadge}>{active.badge}</Text>
              ) : null}
            </View>
            <Text
              style={[styles.name, compact && styles.compactName]}
              numberOfLines={1}
            >
              {active.name}
            </Text>
            <View style={styles.priceRow}>
              <Text style={[styles.price, compact && styles.compactPrice]}>
                {active.pointPrice}
              </Text>
              <Text style={styles.unit}>点</Text>
              <Text style={styles.detailLink}>详情 ›</Text>
            </View>
          </View>
        </Pressable>
        <View style={styles.actions}>
          <Pressable
            style={styles.cartAction}
            onPress={() => addToCart(active)}
          >
            <Feather name="shopping-cart" size={14} color="#e6cb93" />
            <Text style={styles.cartActionText}>加购</Text>
          </Pressable>
          <Pressable style={styles.buyAction} onPress={() => buyNow(active)}>
            <Text style={styles.buyActionText}>点数购买</Text>
          </Pressable>
          {cartCount > 0 ? (
            <Pressable style={styles.cartCount} onPress={openCart}>
              <Text style={styles.cartCountText}>{cartCount}</Text>
            </Pressable>
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  layer: {
    position: "absolute",
    zIndex: 18,
    left: 14,
    right: 74,
    bottom: Platform.OS === "web" ? "13%" : 92,
    alignItems: "flex-start",
  },
  compactLayer: {
    left: 10,
    right: 10,
    bottom: Platform.OS === "web" ? 42 : 76,
  },
  card: {
    width: "100%",
    maxWidth: 410,
    padding: 9,
    paddingRight: 34,
    borderRadius: 14,
    backgroundColor: "rgba(5,20,16,.94)",
    borderWidth: 1,
    borderColor: "rgba(215,182,93,.72)",
    shadowColor: "#000",
    shadowOpacity: 0.45,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 7 },
  },
  compactCard: {
    maxWidth: 350,
    padding: 7,
    paddingRight: 31,
    borderRadius: 11,
  },
  dismiss: {
    position: "absolute",
    zIndex: 2,
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,.08)",
  },
  productMain: { flexDirection: "row", alignItems: "center", gap: 10 },
  image: {
    width: 68,
    height: 68,
    borderRadius: 10,
    backgroundColor: "#14241e",
  },
  compactImage: { width: 54, height: 54, borderRadius: 8 },
  imageFallback: { alignItems: "center", justifyContent: "center" },
  copy: { flex: 1, minWidth: 0 },
  badgeRow: { flexDirection: "row", alignItems: "center", gap: 5 },
  timeBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    overflow: "hidden",
    color: "#07130f",
    backgroundColor: "#28c99a",
    fontSize: 8,
    fontWeight: "900",
  },
  offerBadge: { color: "#e3c884", fontSize: 9, fontWeight: "800" },
  name: { marginTop: 5, color: "#f4f7f6", fontSize: 14, fontWeight: "900" },
  compactName: { fontSize: 12 },
  priceRow: {
    marginTop: 4,
    flexDirection: "row",
    alignItems: "baseline",
    gap: 3,
  },
  price: { color: "#e1bb68", fontSize: 20, fontWeight: "900" },
  compactPrice: { fontSize: 17 },
  unit: { color: "#e1bb68", fontSize: 10, fontWeight: "800" },
  detailLink: { marginLeft: 6, color: "#9ba8a3", fontSize: 10 },
  actions: { marginTop: 7, flexDirection: "row", alignItems: "center", gap: 7 },
  cartAction: {
    height: 30,
    paddingHorizontal: 10,
    borderRadius: 7,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    borderWidth: 1,
    borderColor: "#66502e",
    backgroundColor: "#201b13",
  },
  cartActionText: { color: "#e6cb93", fontSize: 11, fontWeight: "800" },
  buyAction: {
    height: 30,
    paddingHorizontal: 13,
    borderRadius: 7,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#d7b65d",
  },
  buyActionText: { color: "#17120b", fontSize: 11, fontWeight: "900" },
  cartCount: {
    minWidth: 28,
    height: 28,
    paddingHorizontal: 7,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#163c30",
  },
  cartCountText: { color: "#aaf2d7", fontSize: 11, fontWeight: "900" },
});
